import React, { useState, useEffect } from 'react';
import {
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   TextField,
   Button,
   Box,
   IconButton,
   Typography,
   MenuItem,
   Select,
   InputLabel,
   FormControl,
   Alert,
   Snackbar,
   Grid,
   Accordion,
   AccordionSummary,
   AccordionDetails,
   List,
   ListItem,
   ListItemText,
   ListItemSecondaryAction,
   Checkbox,
   FormControlLabel,
   CircularProgress,
   Avatar,
} from '@mui/material';
import { 
   Close as CloseIcon,
   ExpandMore as ExpandMoreIcon,
   Add as AddIcon,
   PhotoLibrary as PhotoLibraryIcon,
   Remove as RemoveIcon,
   Create as CreateIcon
} from '@mui/icons-material';
import { Photo } from '../../services/PhotoService';
import GooglePlacesSearch from '../common/GooglePlacesSearch';
import { Trip } from '../../types/Trip';
import { doc } from 'firebase/firestore';
import { updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { get_all_events_and_shared_events, get_all_trips_and_shared_trips } from '../../services';
import { useUser } from '../../contexts/UserContext';

interface Album {
  id: string;
  title: string;
  description?: string;
  coverPhotoId?: string;
  photoIds?: string[];
  memberIds?: string[];
  createdBy?: string;
  createdAt?: any;
  isPublic?: boolean;
  tripId?: string;
  eventId?: string;
  source?: string;
}

interface EditPhotoDialogProps {
   open: boolean;
   photo: Photo | null;
   onClose: () => void;
}

const EditPhotoDialog: React.FC<EditPhotoDialogProps> = ({ open, photo, onClose }) => {
   const [title, setTitle] = useState('');
   const [description, setDescription] = useState('');
   const [selectedTripId, setSelectedTripId] = useState('');
   const [selectedEventId, setSelectedEventId] = useState('');
   const [location, setLocation] = useState({
      address: '',
      name: '',
      coordinates: {
         latitude: 0,
         longitude: 0,
      },
   });

   const [snackbarMessage, setSnackbarMessage] = useState('');
   const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
   const [snackbarOpen, setSnackbarOpen] = useState(false);
   const [loading, setLoading] = useState(false);
   const [trips, setTrips] = useState<any[]>([]);
   const [events, setEvents] = useState<any[]>([]);
   
   // Album management states
   const [userAlbums, setUserAlbums] = useState<Album[]>([]);
   const [currentAlbums, setCurrentAlbums] = useState<Album[]>([]);
   const [selectedAlbumIds, setSelectedAlbumIds] = useState<Set<string>>(new Set());
   const [loadingAlbums, setLoadingAlbums] = useState(false);
   const [albumsError, setAlbumsError] = useState('');
   const [addingToAlbums, setAddingToAlbums] = useState(false);
   
   // Create new album states
   const [showCreateAlbum, setShowCreateAlbum] = useState(false);
   const [newAlbumTitle, setNewAlbumTitle] = useState('');
   const [newAlbumDescription, setNewAlbumDescription] = useState('');
   const [newAlbumPublic, setNewAlbumPublic] = useState(false);
   const [creatingAlbum, setCreatingAlbum] = useState(false);
   
   const { currentUser } = useUser();

   const loadTripsAndEvents = async () => {
      try {
         const trips = (await get_all_trips_and_shared_trips(
            currentUser?.uid || '',
            currentUser?.email || ''
         )) as any[];
         const events = (await get_all_events_and_shared_events(
            currentUser?.uid || '',
            currentUser?.email || ''
         )) as any[];

         const tripsData: any[] = [];
         const eventsData: any[] = [];

         for (const trip of trips) {
            tripsData.push({
               ...trip,
               id: trip.id,
               title: trip.title,
               startDate: trip.startDate,
               endDate: trip.endDate,
               location: {
                  address: trip.primaryDestination?.address || trip.location?.address || '',
                  name: trip.primaryDestination?.name || trip.location?.name || '',
                  coordinates: {
                     latitude:
                        trip.primaryDestination?.coordinates[0] ||
                        trip.location?.coordinates[0] ||
                        0,
                     longitude:
                        trip.primaryDestination?.coordinates[1] ||
                        trip.location?.coordinates[1] ||
                        0,
                  },
               },
            });
         }

         for (const event of events) {
            eventsData.push({
               ...event,
               id: event.id,
               title: event.title,
               startDate: event.startDate,
               endDate: event.endDate,
               type: event.type,
               location: {
                  address: event.location?.address || '',
                  name: event.location?.name || '',
                  coordinates: {
                     latitude: event.location?.coordinates[0] || 0,
                     longitude: event.location?.coordinates[1] || 0,
                  },
               },
            });
         }

         setTrips(tripsData);
         setEvents(eventsData);
      } catch (error) {
      } finally {
      }
   };

   const loadUserAlbums = async () => {
      if (!currentUser) return;
      
      setLoadingAlbums(true);
      setAlbumsError('');
      
      try {
         const { getAuthHeaders } = await import('../../utils/auth');
         const headers = await getAuthHeaders();

         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/albums/user/${currentUser.uid}`,
            { headers }
         );
         if (!response.ok) {
            throw new Error('Failed to load albums');
         }
         
         const data = await response.json();
         setUserAlbums(data.albums || []);
      } catch (err) {
         console.error('Error loading user albums:', err);
         setAlbumsError('Failed to load albums');
      } finally {
         setLoadingAlbums(false);
      }
   };

   const loadCurrentPhotoAlbums = async () => {
      if (!photo || !currentUser) return;
      
      try {
         // Find albums that contain this photo
         const { getAuthHeaders } = await import('../../utils/auth');
         const headers = await getAuthHeaders();

         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/albums/user/${currentUser.uid}`,
            { headers }
         );
         if (!response.ok) return;
         
         const data = await response.json();
         const albumsWithPhoto = (data.albums || []).filter((album: Album) => 
            album.photoIds && album.photoIds.includes(photo.id)
         );
         
         setCurrentAlbums(albumsWithPhoto);
      } catch (err) {
         console.error('Error loading current photo albums:', err);
      }
   };

   const handleAddToAlbums = async () => {
      if (!photo || selectedAlbumIds.size === 0) return;

      setAddingToAlbums(true);

      try {
         const { getAuthHeaders } = await import('../../utils/auth');
         const headers = await getAuthHeaders();

         const promises = Array.from(selectedAlbumIds).map(async (albumId) => {
            const response = await fetch(
               `${process.env.REACT_APP_API_URL}/api/albums/${albumId}/add-photos`,
               {
                  method: 'POST',
                  headers,
                  body: JSON.stringify({
                     photoIds: [photo.id],
                  }),
               }
            );

            if (!response.ok) {
               throw new Error(`Failed to add to album ${albumId}`);
            }
         });

         await Promise.all(promises);

         // Refresh current albums and clear selection
         await loadCurrentPhotoAlbums();
         setSelectedAlbumIds(new Set());

         setSnackbarMessage(`Photo added to ${selectedAlbumIds.size} album(s)`);
         setSnackbarSeverity('success');
         setSnackbarOpen(true);
      } catch (err) {
         console.error('Error adding to albums:', err);
         setSnackbarMessage('Failed to add photo to albums');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setAddingToAlbums(false);
      }
   };

   const handleRemoveFromAlbum = async (albumId: string) => {
      if (!photo) return;

      try {
         const { getAuthHeaders } = await import('../../utils/auth');
         const headers = await getAuthHeaders();

         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/albums/${albumId}/remove-photo`,
            {
               method: 'POST',
               headers,
               body: JSON.stringify({
                  photoId: photo.id,
               }),
            }
         );

         if (!response.ok) {
            throw new Error('Failed to remove from album');
         }

         // Refresh current albums
         await loadCurrentPhotoAlbums();

         setSnackbarMessage('Photo removed from album');
         setSnackbarSeverity('success');
         setSnackbarOpen(true);
      } catch (err) {
         console.error('Error removing from album:', err);
         setSnackbarMessage('Failed to remove photo from album');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      }
   };

   const handleCreateNewAlbum = async () => {
      if (!newAlbumTitle.trim() || !photo || !currentUser) return;
      
      setCreatingAlbum(true);
      
      try {
         const { getAuthHeaders } = await import('../../utils/auth');
         const headers = await getAuthHeaders();

         const response = await fetch(`${process.env.REACT_APP_API_URL}/api/albums`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
               title: newAlbumTitle.trim(),
               description: newAlbumDescription.trim(),
               createdBy: currentUser.uid,
               photoIds: [photo.id],
               isPublic: newAlbumPublic,
            }),
         });
         
         if (!response.ok) {
            throw new Error('Failed to create album');
         }
         
         // Reset form and refresh albums
         setNewAlbumTitle('');
         setNewAlbumDescription('');
         setNewAlbumPublic(false);
         setShowCreateAlbum(false);
         
         await loadUserAlbums();
         await loadCurrentPhotoAlbums();
         
         setSnackbarMessage('Album created and photo added');
         setSnackbarSeverity('success');
         setSnackbarOpen(true);
      } catch (err) {
         console.error('Error creating album:', err);
         setSnackbarMessage('Failed to create album');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setCreatingAlbum(false);
      }
   };

   const handleAlbumToggle = (albumId: string) => {
      const newSelected = new Set(selectedAlbumIds);
      if (newSelected.has(albumId)) {
         newSelected.delete(albumId);
      } else {
         newSelected.add(albumId);
      }
      setSelectedAlbumIds(newSelected);
   };

   // Filter out albums that already contain the photo
   const availableAlbums = userAlbums.filter(album => 
      !album.photoIds?.includes(photo?.id || '')
   );

   const handleCloseSnackbar = () => {
      setSnackbarOpen(false);
   };

   const handleSave = async () => {
      try {
         if (!photo) return;

         setLoading(true);

         const updatedData: Partial<Photo> = {
            title,
            description,
            location: location
               ? {
                    ...photo.location,
                    name: location.name,
                    address: location.address,
                    coordinates: location.coordinates,
                 }
               : photo.location,
            tripId: selectedTripId ? selectedTripId : photo.tripId || '',
            eventId: selectedEventId ? selectedEventId : photo.eventId || '',
         };

         const photoRef = doc(db, 'photos', photo.id);

         const updates: any = {
            tripId: updatedData.tripId || photo.tripId || '',
            eventId: updatedData.eventId || photo.eventId || '',
            title: updatedData.title || photo.title || '',
            description: updatedData.description || photo.description || '',
            location: updatedData.location || photo.location || '',
         };

         await updateDoc(photoRef, updates);

         setSnackbarMessage('Photo details updated successfully');
         setSnackbarSeverity('success');
         setSnackbarOpen(true);
      } catch (error) {
         setSnackbarMessage('Failed to update photo details');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setLoading(false);
      }
   };

   useEffect(() => {
      loadTripsAndEvents();
   }, []);

   useEffect(() => {
      if (photo) {
         setTitle(photo.title || '');
         setDescription(photo.description || '');
         setSelectedTripId(photo.tripId || '');
         setSelectedEventId(photo.eventId || '');
         setLocation({
            address: photo.location?.address || '',
            name: photo.location?.name || '',
            coordinates: {
               latitude: photo.location?.coordinates?.latitude || 0,
               longitude: photo.location?.coordinates?.longitude || 0,
            },
         });
         
         // Load user albums and current photo albums
         loadUserAlbums();
         loadCurrentPhotoAlbums();
      }
   }, [photo]);

   if (!photo) return null;

   return (
      <>
         <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
            <DialogTitle>
               Edit Photo Details
               <IconButton
                  aria-label='close'
                  onClick={onClose}
                  sx={{ position: 'absolute', right: 8, top: 8 }}
               >
                  <CloseIcon />
               </IconButton>
            </DialogTitle>
            <DialogContent>
               <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                     <Box sx={{ mt: 1 }}>
                        <img
                           src={photo?.url || photo?.thumbnailUrl}
                           alt={photo?.title}
                           style={{
                              width: '100%',
                              maxHeight: '200px',
                              objectFit: 'contain',
                              marginBottom: '16px',
                           }}
                        />

                        <TextField
                           label='Title'
                           fullWidth
                           margin='normal'
                           value={title}
                           onChange={(e) => setTitle(e.target.value)}
                        />

                        <FormControl fullWidth margin='dense'>
                           <InputLabel id='edit-trip-select-label'>Associate with Trip</InputLabel>
                           <Select
                              labelId='edit-trip-select-label'
                              value={selectedTripId || ''}
                              onChange={(e) => {
                                 const tripId = e.target.value as string;
                                 setSelectedTripId(tripId);
                              }}
                              label='Associate with Trip'
                           >
                              <MenuItem value=''>
                                 <em>None</em>
                              </MenuItem>
                              {trips.map((trip) => (
                                 <MenuItem key={trip.id} value={trip.id}>
                                    {trip.title}
                                 </MenuItem>
                              ))}
                           </Select>
                        </FormControl>

                        <FormControl fullWidth margin='dense'>
                           <InputLabel id='edit-event-select-label'>Associate with Event</InputLabel>
                           <Select
                              labelId='edit-event-select-label'
                              value={selectedEventId || ''}
                              onChange={(e) => {
                                 const eventId = e.target.value as string;
                                 setSelectedEventId(eventId);
                              }}
                              label='Associate with Event'
                           >
                              <MenuItem value=''>
                                 <em>None</em>
                              </MenuItem>
                              {events.map((event) => (
                                 <MenuItem key={event.id} value={event.id}>
                                    {event.title}
                                 </MenuItem>
                              ))}
                           </Select>
                        </FormControl>

                        <TextField
                           label='Description'
                           fullWidth
                           margin='normal'
                           multiline
                           rows={3}
                           value={description}
                           onChange={(e) => setDescription(e.target.value)}
                        />

                        <GooglePlacesSearch
                           onLocationSelect={(location: any) => {
                              setLocation(location);
                           }}
                           defaultValue={location.name}
                           searchType="places"
                           label="Location"
                        />
                     </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                     <Typography variant="h6" gutterBottom>
                        <PhotoLibraryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Album Management
                     </Typography>
                     
                     {/* Current Albums */}
                     <Accordion defaultExpanded>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                           <Typography variant="subtitle1">
                              Current Albums ({currentAlbums.length})
                           </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                           {currentAlbums.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">
                                 This photo is not in any albums yet.
                              </Typography>
                           ) : (
                              <List dense>
                                 {currentAlbums.map((album) => (
                                    <ListItem key={album.id}>
                                       <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                                          <PhotoLibraryIcon />
                                       </Avatar>
                                       <ListItemText
                                          primary={album.title}
                                          secondary={`${album.photoIds?.length || 0} photos`}
                                       />
                                       <ListItemSecondaryAction>
                                          <IconButton
                                             edge="end"
                                             onClick={() => handleRemoveFromAlbum(album.id)}
                                             size="small"
                                             color="error"
                                          >
                                             <RemoveIcon />
                                          </IconButton>
                                       </ListItemSecondaryAction>
                                    </ListItem>
                                 ))}
                              </List>
                           )}
                        </AccordionDetails>
                     </Accordion>
                     
                     {/* Add to Albums */}
                     <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                           <Typography variant="subtitle1">
                              Add to Albums ({availableAlbums.length} available)
                           </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                           {loadingAlbums ? (
                              <Box display="flex" justifyContent="center" p={2}>
                                 <CircularProgress size={24} />
                              </Box>
                           ) : albumsError ? (
                              <Alert severity="error">{albumsError}</Alert>
                           ) : availableAlbums.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">
                                 No available albums. Create a new one below.
                              </Typography>
                           ) : (
                              <>
                                 <List dense>
                                    {availableAlbums.map((album) => (
                                       <ListItem key={album.id}>
                                          <FormControlLabel
                                             control={
                                                <Checkbox
                                                   checked={selectedAlbumIds.has(album.id)}
                                                   onChange={() => handleAlbumToggle(album.id)}
                                                />
                                             }
                                             label={
                                                <Box>
                                                   <Typography variant="body2">{album.title}</Typography>
                                                   <Typography variant="caption" color="text.secondary">
                                                      {album.photoIds?.length || 0} photos
                                                   </Typography>
                                                </Box>
                                             }
                                          />
                                       </ListItem>
                                    ))}
                                 </List>
                                 
                                 {selectedAlbumIds.size > 0 && (
                                    <Box mt={2}>
                                       <Button
                                          variant="contained"
                                          onClick={handleAddToAlbums}
                                          disabled={addingToAlbums}
                                          startIcon={addingToAlbums ? <CircularProgress size={16} /> : <AddIcon />}
                                          fullWidth
                                       >
                                          Add to {selectedAlbumIds.size} Album(s)
                                       </Button>
                                    </Box>
                                 )}
                              </>
                           )}
                        </AccordionDetails>
                     </Accordion>
                     
                     {/* Create New Album */}
                     <Accordion expanded={showCreateAlbum} onChange={() => setShowCreateAlbum(!showCreateAlbum)}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                           <Typography variant="subtitle1">
                              <CreateIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                              Create New Album
                           </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                           <Box component="form" onSubmit={(e) => { e.preventDefault(); handleCreateNewAlbum(); }}>
                              <TextField
                                 label="Album Title"
                                 fullWidth
                                 margin="normal"
                                 value={newAlbumTitle}
                                 onChange={(e) => setNewAlbumTitle(e.target.value)}
                                 required
                              />
                              
                              <TextField
                                 label="Description (optional)"
                                 fullWidth
                                 margin="normal"
                                 multiline
                                 rows={2}
                                 value={newAlbumDescription}
                                 onChange={(e) => setNewAlbumDescription(e.target.value)}
                              />
                              
                              <FormControlLabel
                                 control={
                                    <Checkbox
                                       checked={newAlbumPublic}
                                       onChange={(e) => setNewAlbumPublic(e.target.checked)}
                                    />
                                 }
                                 label="Make album public"
                                 sx={{ mt: 1 }}
                              />
                              
                              <Button
                                 type="submit"
                                 variant="contained"
                                 fullWidth
                                 disabled={!newAlbumTitle.trim() || creatingAlbum}
                                 startIcon={creatingAlbum ? <CircularProgress size={16} /> : <CreateIcon />}
                                 sx={{ mt: 2 }}
                              >
                                 Create Album & Add Photo
                              </Button>
                           </Box>
                        </AccordionDetails>
                     </Accordion>
                  </Grid>
               </Grid>
            </DialogContent>
            <DialogActions>
               <Button onClick={onClose}>Cancel</Button>
               <Button onClick={handleSave} color='primary' variant='contained' disabled={loading}>
                  {loading ? 'Saving...' : 'Save Changes'}
               </Button>
            </DialogActions>
         </Dialog>

         <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
         >
            <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
               {snackbarMessage}
            </Alert>
         </Snackbar>
      </>
   );
};

export default EditPhotoDialog;
